import {
  useRef,
  useCallback,
  useMemo,
  useEffect,
  useState,
  useSyncExternalStore,
  MutableRefObject,
} from 'react';
import {
  FileType,
  isUnsavedFileType,
} from '../graphicUploader/types';
import { MainImage } from '../mainImage';
import { isUsableSource } from 'shared/utils/sourceUrl';

export type FileSetForDisplay<T = object> = FileType & T;
export type DisplayedFile<T = object> = FileType & {
  loading: boolean;
} & T;

export interface DisplayedFilesStore<T = object> {
  get: () => DisplayedFile<T>[];
  set: (value: (FileType & T)[]) => void;
  subscribe: (callback: () => void) => () => void;
}

function inferDisplayedFileLoading(file: FileType): boolean {
  if (isUnsavedFileType(file)) {
    return false;
  }

  if (
    isUsableSource(file.downloadSrc) ||
    isUsableSource(file.src) ||
    isUsableSource(file.thumbnail)
  ) {
    return false;
  }

  return !file.signedRequest;
}

export function setFileRotationInStore<T = object>(
  store: DisplayedFilesStore<T>,
  index: number,
  imageRotation: number
): void {
  const files = store.get();
  if (index < 0 || index >= files.length) return;
  const next = files.slice();
  next[index] = { ...next[index], imageRotation } as DisplayedFile<T>;
  store.set(next);
}

export function useStoreFiles<T>(
  initialFiles?: DisplayedFile<T>[]
): DisplayedFilesStore<T> {
  const filesRef = useRef<DisplayedFile<T>[]>(initialFiles || []);
  const get = useCallback(() => {
    return filesRef.current;
  }, []);

  const subscribers = useRef(new Set<() => void>());

  const set = useCallback((value: (FileType & T)[]) => {
    const valueWithLoading: DisplayedFile<T>[] = value.map((file) => {
      if ((file as DisplayedFile<T>).loading === undefined) {
        (file as DisplayedFile<T>).loading = inferDisplayedFileLoading(file);
      }
      return file as DisplayedFile<T>;
    });
    filesRef.current = valueWithLoading;
    subscribers.current.forEach((callback) => callback());
  }, []);

  const subscribe = useCallback((callback: () => void) => {
    subscribers.current.add(callback);
    return () => subscribers.current.delete(callback);
  }, []);

  return useMemo(
    () => ({
      get,
      set,
      subscribe,
    }),
    [get, set, subscribe]
  );
}

/** Mirrors ListImage, which skips only files flagged `deleted`. */
function isVisibleDisplayedFile(file: { deleted?: boolean }): boolean {
  return file.deleted !== true;
}

/** Reactive presence of files that the gallery actually renders. */
export function useDisplayedFilesStoreHasFiles(
  filesStore: DisplayedFilesStore | undefined
): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      filesStore ? filesStore.subscribe(onStoreChange) : () => {},
    [filesStore]
  );
  const getSnapshot = useCallback(
    () => (filesStore?.get().some(isVisibleDisplayedFile) ?? false),
    [filesStore]
  );
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

function identity<T>(x: T): T {
  return x;
}

export function makeDisplayedFilesStore<K = object>(
  filesStore: DisplayedFilesStore<K>,
  sort: (files: DisplayedFile<K>[]) => DisplayedFile<K>[] = identity,
  mainImageHandlerRef?: MutableRefObject<MainImage>  
) {
  return function displayFilesWithSelector<T>(
    selector: (store: DisplayedFile<K>[]) => T
  ): [T, (value: DisplayedFile<K>[]) => void] {
    const [state, setState] = useState(selector(sort(filesStore.get())));

    useEffect(() => {
      setState(selector(sort(filesStore.get())));
      return filesStore.subscribe(() =>
        setState(selector(sort(filesStore.get())))
      );
    }, [selector]);

    useEffect(() => {
      if (!mainImageHandlerRef) return;
      setState(selector(sort(filesStore.get().slice())));
      return mainImageHandlerRef.current
        .getMainImageStore()
        .subscribe(() =>
          setState(selector(sort(filesStore.get().slice())))
        );
    }, [selector]);

    return [state, filesStore.set];
  };
}
